import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useOrder } from '../../contexts/OrderContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiCheck, FiAlertCircle, FiRefreshCw } = FiIcons;

const KitchenDisplay = () => {
  const { getOrdersByStatus, updateOrderStatus, loadOrders, isLoading } = useOrder();
  const [currentTime, setCurrentTime] = useState(new Date());

  const activeOrders = [
    ...getOrdersByStatus('pending'),
    ...getOrdersByStatus('preparing')
  ].sort((a, b) => new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at));

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // Update every 30 seconds
    return () => clearInterval(timer);
  }, []);

  // Auto-refresh orders every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadOrders();
    }, 30000);

    return () => clearInterval(interval);
  }, [loadOrders]);

  const getOrderAge = (createdAt) => {
    const now = new Date();
    const orderTime = new Date(createdAt);
    const diffInMinutes = Math.floor((now - orderTime) / (1000 * 60));
    return diffInMinutes;
  };

  const getOrderPriority = (order) => {
    const age = getOrderAge(order.createdAt || order.created_at);
    if (age > 20) return 'urgent';
    if (age > 15) return 'high';
    return 'normal';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-red-500 bg-red-50';
      case 'high': return 'border-orange-500 bg-orange-50';
      default: return 'border-warm-brown/20 bg-white';
    }
  };

  const handleStatusChange = (orderId, newStatus) => {
    updateOrderStatus(orderId, newStatus);
  };

  const handleManualRefresh = () => {
    loadOrders();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Kitchen Display</h2>
          <p className="text-warm-brown/70">Active orders for preparation</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleManualRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors disabled:opacity-50"
          >
            <SafeIcon icon={FiRefreshCw} className={`text-sm ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <div className="bg-white rounded-lg px-4 py-2 shadow-lg">
            <p className="text-warm-brown font-medium">
              {currentTime.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <SafeIcon icon={FiRefreshCw} className="text-6xl text-warm-brown/30 mx-auto mb-4 animate-spin" />
          <p className="text-warm-brown/70 text-xl">Loading orders...</p>
        </div>
      ) : activeOrders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <SafeIcon icon={FiCheck} className="text-6xl text-green-500 mx-auto mb-4" />
          <p className="text-warm-brown/70 text-xl">All caught up!</p>
          <p className="text-warm-brown/50 text-sm">No orders in the kitchen queue</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {activeOrders.map((order, index) => {
            const priority = getOrderPriority(order);
            const orderAge = getOrderAge(order.createdAt || order.created_at);

            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`rounded-xl shadow-lg border-2 overflow-hidden ${getPriorityColor(priority)}`}
              >
                {/* Header */}
                <div className="p-4 border-b border-warm-brown/10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-playfair text-warm-brown font-bold">
                      #{order.orderNumber || order.order_number}
                    </h3>
                    <div className="flex items-center gap-2">
                      {priority === 'urgent' && (
                        <SafeIcon icon={FiAlertCircle} className="text-red-500 text-lg animate-pulse" />
                      )}
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'pending'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {order.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-warm-brown/70">
                    <div className="flex items-center gap-1">
                      <SafeIcon icon={FiClock} className="text-xs" />
                      <span>{orderAge}m ago</span>
                    </div>
                    <div>
                      <span className="font-medium">
                        {order.customerInfo?.name || order.customer_info?.name}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="p-4 space-y-3">
                  {(order.items || []).map((item, itemIndex) => (
                    <div key={itemIndex} className="bg-cream rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-warm-brown text-lg">
                          {item.quantity}x {item.name}
                        </h4>
                      </div>
                      {/* Customizations for Kitchen */}
                      {item.customizations && (
                        <div className="space-y-1 text-sm">
                          {item.customizations.bread && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-warm-brown">Bread:</span>
                              <span className="bg-white px-2 py-1 rounded text-warm-brown">
                                {item.customizations.bread}
                              </span>
                            </div>
                          )}
                          {item.customizations.protein && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-warm-brown">Protein:</span>
                              <span className="bg-white px-2 py-1 rounded text-warm-brown">
                                {item.customizations.protein}
                              </span>
                            </div>
                          )}
                          {item.customizations.toppings?.length > 0 && (
                            <div>
                              <span className="font-medium text-warm-brown block mb-1">Toppings:</span>
                              <div className="flex flex-wrap gap-1">
                                {item.customizations.toppings.map((topping, tIndex) => (
                                  <span
                                    key={tIndex}
                                    className="bg-white px-2 py-1 rounded text-xs text-warm-brown"
                                  >
                                    {topping}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {item.customizations.extras?.length > 0 && (
                            <div>
                              <span className="font-medium text-warm-brown block mb-1">Extras:</span>
                              <div className="flex flex-wrap gap-1">
                                {item.customizations.extras.map((extra, eIndex) => (
                                  <span
                                    key={eIndex}
                                    className="bg-gold-accent text-white px-2 py-1 rounded text-xs"
                                  >
                                    {extra}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Special Instructions */}
                  {(order.customerInfo?.specialInstructions || order.customer_info?.specialInstructions) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <h4 className="font-medium text-yellow-800 mb-1">⚠️ Special Instructions:</h4>
                      <p className="text-sm text-yellow-700">
                        {order.customerInfo?.specialInstructions || order.customer_info?.specialInstructions}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="p-4 border-t border-warm-brown/10">
                  {order.status === 'pending' ? (
                    <button
                      onClick={() => handleStatusChange(order.id, 'preparing')}
                      className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                    >
                      Start Preparing
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStatusChange(order.id, 'ready')}
                      className="w-full bg-green-500 text-white py-3 rounded-lg font-medium hover:bg-green-600 transition-colors"
                    >
                      Mark Ready
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KitchenDisplay;